import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Search,
  Building2,
  FileText,
  CalendarDays,
  LayoutGrid,
  Map as MapIcon,
  Users,
  Package,
  MapPin,
  BarChart3,
  UserCog,
  Activity,
} from 'lucide-react';
import clsx from 'clsx';
import { clientsAPI, serviceCallsAPI } from '@shared/apiClient';
import { getServiceCallListCustomerName } from '@shared/utils';
import { ZONES, DISPATCH_VIEWS } from '../utils/zoneNav';
import { useShell } from './ShellContext';

const STATIC_ACTIONS = [
  { id: 'zone-dispatch', label: 'Go to Dispatch', icon: CalendarDays, action: 'zone', zone: 'dispatch', path: '/dispatch' },
  { id: 'zone-invoicing', label: 'Go to Invoicing', icon: FileText, action: 'zone', zone: 'invoicing', path: '/invoicing' },
  { id: 'zone-management', label: 'Go to Management', icon: Users, action: 'zone', zone: 'management', path: '/customers' },
  { id: 'view-calendar', label: 'Dispatch: Calendar view', icon: CalendarDays, action: 'dispatchView', view: 'calendar', path: '/dispatch' },
  { id: 'view-board', label: 'Dispatch: Board view', icon: LayoutGrid, action: 'dispatchView', view: 'board', path: '/dispatch' },
  { id: 'view-map', label: 'Dispatch: Map view', icon: MapIcon, action: 'dispatchView', view: 'map', path: '/dispatch' },
  { id: 'view-today', label: 'Dispatch: Today view', icon: Activity, action: 'dispatchView', view: 'today', path: '/dispatch' },
  { id: 'customers', label: 'Customers & Sites', icon: Building2, path: '/customers' },
  { id: 'tech-scheduling', label: 'Technician Scheduling', icon: UserCog, path: '/tech-scheduling' },
  { id: 'reports', label: 'Reports', icon: BarChart3, path: '/reports' },
  { id: 'inventory', label: 'Inventory', icon: Package, path: '/inventory' },
  { id: 'inventory-locations', label: 'Inventory Locations', icon: MapPin, path: '/inventory-locations' },
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const {
    commandPaletteOpen,
    setCommandPaletteOpen,
    setActiveZone,
    setDispatchView,
  } = useShell();

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { data: clientsData } = useQuery(
    ['commandPaletteClients', query],
    () => clientsAPI.getClients({ search: query, limit: 8, isActive: true }),
    { enabled: commandPaletteOpen && query.trim().length >= 2, staleTime: 30000 }
  );

  const { data: callsData } = useQuery(
    ['commandPaletteCalls', query],
    () => serviceCallsAPI.getServiceCalls({ search: query, limit: 8 }),
    { enabled: commandPaletteOpen && query.trim().length >= 2, staleTime: 30000 }
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = [];

    STATIC_ACTIONS.forEach((action) => {
      if (!q || action.label.toLowerCase().includes(q)) {
        items.push({ type: 'action', ...action });
      }
    });

    if (q.length >= 2) {
      (clientsData?.data?.clients || []).forEach((client) => {
        const name = client.isCommercial && client.companyName
          ? client.companyName
          : [client.contactLastName, client.contactFirstName].filter(Boolean).join(', ');
        items.push({
          type: 'client',
          id: `client-${client.id}`,
          label: name || 'Customer',
          subLabel: [client.city, client.state].filter(Boolean).join(', '),
          icon: Building2,
          path: '/customers',
          clientId: client.id,
        });
      });

      (callsData?.data?.serviceCalls || []).forEach((call) => {
        items.push({
          type: 'serviceCall',
          id: `call-${call.id}`,
          label: getServiceCallListCustomerName(call),
          subLabel: `${call.status}${call.invoiceNumber ? ` · #${call.invoiceNumber}` : ''}`,
          icon: FileText,
          path: '/service-calls',
          serviceCallId: call.id,
        });
      });
    }

    return items.slice(0, 12);
  }, [query, clientsData, callsData]);

  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [commandPaletteOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const runItem = (item) => {
    if (item.action === 'zone') {
      setActiveZone(item.zone);
    }
    if (item.action === 'dispatchView') {
      setActiveZone('dispatch');
      setDispatchView(item.view);
    }
    navigate(item.path || '/dispatch');
    setCommandPaletteOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      runItem(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setCommandPaletteOpen(false);
    }
  };

  if (!commandPaletteOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/40"
        aria-label="Close command palette"
        onClick={() => setCommandPaletteOpen(false)}
      />
      <div className="relative w-full max-w-xl bg-surface rounded-lg border border-surface-border shadow-xl overflow-hidden">
        <div className="flex items-center gap-2 px-3 border-b border-surface-border">
          <Search className="h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search customers, service calls, or jump to..."
            className="flex-1 py-3 text-sm bg-transparent border-0 focus:outline-none focus:ring-0"
          />
          <kbd className="text-2xs text-gray-400 border border-surface-border rounded px-1">esc</kbd>
        </div>

        <ul className="max-h-80 overflow-auto py-1">
          {results.length === 0 ? (
            <li className="px-4 py-6 text-sm text-gray-500 text-center">No results</li>
          ) : (
            results.map((item, index) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => runItem(item)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
                      index === selectedIndex ? 'bg-primary-50' : 'hover:bg-surface-hover'
                    )}
                  >
                    <Icon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{item.label}</div>
                      {item.subLabel && (
                        <div className="text-xs text-gray-500 truncate">{item.subLabel}</div>
                      )}
                    </div>
                    {item.zone && (
                      <span className="text-2xs text-gray-400">{ZONES[item.zone]?.label}</span>
                    )}
                    {item.view && (
                      <span className="text-2xs text-gray-400">{DISPATCH_VIEWS[item.view]?.label}</span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
